import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import {
  formatFileSize,
  isAudioMime,
  isImageMime,
  isPdfMime,
  isVideoMime,
  type FileAttachment,
} from '@avslack/shared';
import { Icon } from './Icon';
import { useBackToClose } from '../lib/back-to-close';

/** A PDF-nezo a pdf.js-sel egyutt csak megnyitaskor toltodik be. */
const PdfViewer = lazy(() => import('./PdfViewer').then((m) => ({ default: m.PdfViewer })));

/**
 * Uzenethez csatolt fajlok.
 *
 * A kepek racsban, kattintasra nagyban; a videok es hangok beagyazva; minden
 * mas kartyakent, letoltessel. A tipust a MIME dontsi el, nem a kiterjesztes.
 */
export function Attachments({ files }: { files: FileAttachment[] }) {
  const [lightbox, setLightbox] = useState<FileAttachment | null>(null);

  if (files.length === 0) return null;

  const images = files.filter((f) => isImageMime(f.mime));
  const others = files.filter((f) => !isImageMime(f.mime));

  return (
    <div className="mt-2 space-y-2">
      {images.length > 0 && (
        <div
          className={[
            'grid gap-1.5',
            images.length === 1 ? 'max-w-sm' : 'max-w-lg grid-cols-2',
          ].join(' ')}
        >
          {/*
            A KEP ARANYA MARAD - kicsinyitunk, nem vagunk.

            Korabban `object-cover` allt itt, teljes szelesseggel: egy allo
            kepbol csak a kozepe latszott, a feliratnak es a fejeknek a fele
            lemaradt. Egy kepnel a sajat aranyaban jelenik meg (a `width` /
            `height` attributumbol a bongeszo betoltes elott is tudja az
            aranyt, tehat nem ugrik a folyam); racsban a cella egyseges, a kep
            benne egeszben latszik.
          */}
          {images.map((file) => (
            <button
              key={file.id}
              type="button"
              onClick={() => setLightbox(file)}
              className={[
                'group/img overflow-hidden rounded-control border border-border-soft bg-surface-sunken transition-colors duration-(--duration-quick) hover:border-border-strong',
                images.length === 1 ? 'w-fit max-w-full' : 'grid h-48 place-items-center',
              ].join(' ')}
            >
              <img
                src={file.thumbUrl ?? file.url}
                alt={file.name}
                loading="lazy"
                width={file.width ?? undefined}
                height={file.height ?? undefined}
                className={[
                  'object-contain transition-transform duration-(--duration-base) ease-(--ease-soft) group-hover/img:scale-[1.01]',
                  images.length === 1
                    ? 'block h-auto max-h-80 w-auto max-w-full'
                    : 'h-auto max-h-full w-auto max-w-full',
                ].join(' ')}
              />
            </button>
          ))}
        </div>
      )}

      {others.map((file) => (
        <AttachmentCard key={file.id} file={file} />
      ))}

      {lightbox && <Lightbox file={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function AttachmentCard({ file }: { file: FileAttachment }) {
  const [pdfOpen, setPdfOpen] = useState(false);

  if (isVideoMime(file.mime)) {
    /*
      POSZTERKEP (versenyaudit K3): ha van, a video csak lejatszaskor tolt.
      Enelkul minden uzenetfolyam-gorgetes elkezdte huzni a videok elejet,
      csak hogy legyen mit mutatni - es iPhone-on akkor is fekete maradt.
    */
    return (
      <video
        controls
        preload={file.thumbUrl ? 'none' : 'metadata'}
        poster={file.thumbUrl ?? undefined}
        src={file.url}
        width={file.width ?? undefined}
        height={file.height ?? undefined}
        className="h-auto max-h-80 w-full max-w-lg rounded-control border border-border-soft bg-black"
      />
    );
  }

  if (isAudioMime(file.mime)) {
    return <audio controls preload="metadata" src={file.url} className="w-full max-w-sm" />;
  }

  /*
    MEGNYITAS es LETOLTES kulon.

    Eddig az egesz kartya egyetlen link volt, ami megnyitotta a fajlt - es hogy
    a "megnyitas" mit jelent, azt a bongeszo dontotte el fajltipusonkent. Egy
    PDF megnyilt, egy Markdown vagy megjelent nyersen, vagy letoltodott, egy
    Word-fajl pedig hol igy, hol ugy. Aki menteni akart, annak nem volt mit
    csinalnia.

    Mostantol a kartya tovabbra is megnyit, a jobb szelen levo gomb pedig
    MENT - ugyanaz az utvonal, csak `?download=1`-gyel, ami `attachment`
    tartalom-elhelyezest ker a tarolotol.
  */
  const pdf = isPdfMime(file.mime);
  const label = (
    <>
      <span
        aria-hidden="true"
        className="grid size-9 shrink-0 place-items-center rounded-control bg-surface-sunken text-sm"
      >
        <Icon name={pdf ? 'fajl' : 'csatolmany'} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{file.name}</span>
        <span className="block text-2xs text-ink-faint">{formatFileSize(file.size)}</span>
      </span>
    </>
  );

  return (
    <div className="max-w-md overflow-hidden rounded-control border border-border-soft bg-surface transition-[border-color,box-shadow] duration-(--duration-base) ease-(--ease-soft) hover:border-border-strong hover:shadow-(--shadow-soft)">
      {/*
        PDF ELSO OLDALA (versenyaudit K3) - ha a feltolto bongeszoje
        elkeszitette. Ranezesre latszik, melyik szerzodes vagy lelet ez.
      */}
      {pdf && file.thumbUrl && (
        <button
          type="button"
          onClick={() => setPdfOpen(true)}
          title="Megnyitás"
          className="block w-full border-b border-border-soft bg-surface-sunken"
        >
          <img
            src={file.thumbUrl}
            alt={`${file.name} – első oldal`}
            loading="lazy"
            width={file.width ?? undefined}
            height={file.height ?? undefined}
            className="mx-auto block h-auto max-h-64 w-auto max-w-full object-contain object-top"
          />
        </button>
      )}
      <div className="flex items-center gap-1 pr-1">
        {pdf ? (
          <button
            type="button"
            onClick={() => setPdfOpen(true)}
            title="Megnyitás"
            className="flex min-w-0 flex-1 items-center gap-3 rounded-control px-3 py-2.5 text-left"
          >
            {label}
          </button>
        ) : (
          <a
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Megnyitás"
            className="flex min-w-0 flex-1 items-center gap-3 rounded-control px-3 py-2.5"
          >
            {label}
          </a>
        )}

        <a
          href={`${file.url}?download=1`}
          title={`Letöltés: ${file.name}`}
          aria-label={`Letöltés: ${file.name}`}
          className="grid size-9 shrink-0 place-items-center rounded-control text-ink-faint transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-ink"
        >
          <Icon name="letoltes" />
        </a>
      </div>

      {pdfOpen && (
        <Suspense fallback={null}>
          <PdfViewer file={file} onClose={() => setPdfOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}

function Lightbox({ file, onClose }: { file: FileAttachment; onClose: () => void }) {
  useBackToClose(true, onClose);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  /*
    A FOKUSZ bekerul a nagykepbe, es bezaraskor visszater oda, ahonnan jott.

    Enelkul a billentyuzetes hasznalo a sotet reteg ALATT, a lathatatlan
    uzeneteken tabolt tovabb, a kepernyoolvaso pedig nem vette eszre, hogy
    parbeszed nyilt.
  */
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previous?.focus();
  }, []);

  // A Tab a nagykep ket gombja kozott korbe jar, nem szokik ki alola.
  const trapTab = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button');
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      ref={dialogRef}
      onKeyDown={trapTab}
      role="dialog"
      aria-label={file.name}
      aria-modal="true"
      onClick={onClose}
      className="animate-fade-in fixed inset-0 z-50 flex flex-col bg-black/85 p-4"
    >
      <div className="flex items-center gap-3 pb-3 text-sm text-white/80">
        <span className="truncate font-medium">{file.name}</span>
        <span className="shrink-0 text-white/50">{formatFileSize(file.size)}</span>
        {/* A nagykepbol MENTENI akarunk - nem ujra megnyitni ugyanazt. */}
        <a
          href={`${file.url}?download=1`}
          onClick={(e) => e.stopPropagation()}
          className="ml-auto flex shrink-0 items-center gap-1.5 rounded-control px-2.5 py-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          <Icon name="letoltes" size="sm" />
          Letöltés
        </a>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Bezárás"
          className="shrink-0 rounded-control px-2 py-1 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          <Icon name="bezaras" />
        </button>
      </div>

      {/*
        A kep MELLE kattintas bezar.

        Korabban a kep eleme toltotte ki a teljes feluletet (a kep csak
        kozepre volt igazitva benne), igy a "mellette" kattintas is a kepre
        esett, es nem zart be. Most a doboz akkora, mint maga a kep - a korulotte
        levo sotet resz a hatter, ami bezar.
      */}
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <img
          src={file.url}
          alt={file.name}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    </div>
  );
}

/** A szerkesztoben varakozo feltoltesek, elorehaladassal. */
export function UploadTray({
  uploads,
  onRemove,
}: {
  uploads: {
    key: string;
    name: string;
    size: number;
    progress: number;
    error: string | null;
    previewUrl: string | null;
    attachment: FileAttachment | null;
  }[];
  onRemove: (key: string) => void;
}) {
  if (uploads.length === 0) return null;

  return (
    <div className="mb-2 flex flex-wrap gap-2 border-b border-border-soft pb-2">
      {uploads.map((upload) => (
        <div
          key={upload.key}
          className={[
            'relative flex w-52 items-center gap-2 rounded-control border px-2 py-1.5',
            upload.error ? 'border-danger/40 bg-danger-soft' : 'border-border-soft bg-surface',
          ].join(' ')}
        >
          {upload.previewUrl ? (
            <img src={upload.previewUrl} alt="" className="size-8 shrink-0 rounded object-cover" />
          ) : (
            <span
              aria-hidden="true"
              className="grid size-8 shrink-0 place-items-center rounded bg-surface-sunken text-xs"
            >
              <Icon name="csatolmany" />
            </span>
          )}

          <span className="min-w-0 flex-1">
            <span className="block truncate text-2xs font-medium">{upload.name}</span>
            {upload.error ? (
              <span role="alert" className="block truncate text-2xs text-danger">
                {upload.error}
              </span>
            ) : upload.attachment ? (
              <span className="block text-2xs text-ink-faint">{formatFileSize(upload.size)}</span>
            ) : (
              <span
                role="progressbar"
                aria-label={`${upload.name} feltöltése`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(upload.progress)}
                className="mt-1 block h-1 overflow-hidden rounded-pill bg-surface-sunken"
              >
                <span
                  className="block h-full rounded-pill bg-accent transition-[width] duration-(--duration-base)"
                  style={{ width: `${upload.progress}%` }}
                />
              </span>
            )}
          </span>

          <button
            type="button"
            onClick={() => onRemove(upload.key)}
            aria-label={`${upload.name} eltávolítása`}
            className="shrink-0 rounded px-1 text-ink-faint transition-colors hover:text-ink"
          >
            <Icon name="bezaras" />
          </button>
        </div>
      ))}
    </div>
  );
}
